import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandDataOut,
  CommandQueryDataIn,
  CommandStatus,
  PageInfo,
  QueryTokenDataIn,
  QueryTokenDataOut,
  SequentalTokenDataOut,
  SortingType,
  TokenStatus,
} from '../../../api/api-reference';
import { PermissionsEnum } from '../../../api/api-reference';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { StatusComponent } from '../../core/components/status/status.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { TokenRetryDialogComponent } from '../../core/components/token-retry-dialog/token-retry-dialog.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { processDateRange } from '../../../helper/date-utils';

@Component({
  selector: 'app-clear-tamper',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterLink,
    SortComponent,
    PaginationComponent,
    SearchComponent,
    DatepickerComponent,
    IsNewDirective,
    StatusComponent,
    AngularSvgIconModule,
    TokenRetryDialogComponent,
  ],
  templateUrl: './clear-tamper.component.html',
  styleUrl: './clear-tamper.component.scss',
})
export class ClearTamperComponent extends SignalRBaseComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public selectedCommand: CommandDataOut | undefined | null;
  data: QueryTokenDataOut[] | undefined = [];
  readonly dayInMs = 24 * 60 * 60 * 1000;
  isDatepickerOpen = false;
  tokenStatuses = Object.values(TokenStatus).map(s => s.toUpperCase());
  isModalOpen = false;
  retryCommandId: string | undefined = undefined
  sequentialTokens: SequentalTokenDataOut[] = [];
  
  public PermissionsEnum = PermissionsEnum;
  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  requestData = new QueryTokenDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
  });

  columns: { header: string; key: keyof QueryTokenDataOut }[] = [
    { header: 'DEVICESETUP_ASSETTYPE', key: 'assetType' },
    { header: 'DEVICESETUP_SERVICENAME', key: 'serviceName' },
    { header: 'DEVICESETUP_METERNO', key: 'meterNo' },
    { header: 'REPORT_TOKEN_NUMBER', key: 'token' },
    { header: 'COMMAND_SOURCE', key: 'source' },
    { header: 'DATE', key: 'startTime' },
    { header: 'TIME', key: 'startTime' },
    { header: 'TOKEN_STATUS', key: 'tokenStatus' },
    { header: 'COMMAND_STATUS', key: 'commandStatus' },
  ];

  protected override registerHandlers(): void {
    this.registerSignalREvent('load-clear-tamper-token', (message) => {
      const item = this.data?.find((item) => item.id === message.tokenId);
      if (item) {
        item.tokenStatus = message.tokenStatus;
        item.commandStatus = message.commandStatus;
        item.failCount = message.failCount;
        if (message.tokenStatus == TokenStatus.Failed) {
          try {
            const parsed = JSON.parse(message.errorMessage);
            item.errorMessage = parsed.Message;
          } catch {
            item.errorMessage = message.errorMessage;
          }
        }
      }
    });

    // Handle online-status silently
    this.registerSignalREvent('online-status', () => {});
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  printResponseOfCommand(event: MouseEvent, cmd: QueryTokenDataOut) {
    if (cmd.tokenStatus === TokenStatus.Failed) {
      event.stopPropagation();
      this.toastr.error(cmd.errorMessage);
    }
  }

  retryCommand(entity: QueryTokenDataOut) {
    if (entity.executionOrder != null) {
      this.retryCommandId = entity.commandId;
      
      this.client.getTokensByCommand(entity.commandId).subscribe({
        next: (response) => {
          this.sequentialTokens = response;
          this.isModalOpen = true;
        },
        error: (err: string) => {
          this.toastr.error('Failed to retry command: ' + err);
        },
      });
      
    }
    else {
      this.getRetryData(entity.commandId);
    }
  }

  getRetryData(commandId: string) {
    this.client.onDemandClearTamperRetry(commandId).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err: string) => {
        this.toastr.error('Failed to retry command: ' + err);
      },
    });
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as TokenStatus);
    }
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  ensureDate(dateTime: unknown): Date | null {
    return dateTime instanceof Date ? dateTime : null;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.clearTamperQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.selectedCommand = null;
  }

  filterDatesInTable() {
    this.client.clearTamperQuery(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  
  isMaxCommandReached(cmd: QueryTokenDataOut): boolean {
    return (cmd.failCount || 0) >= (cmd.maxRetryCount || 0);
  }

  getCommandTooltipText(cmd: QueryTokenDataOut): string {
    return `Retry ${cmd.failCount || 0} / ${cmd.maxRetryCount || 0}`;
  }
}
